// AUTH-02. One list of the paths a logged-out visitor is allowed to be on.
//
// WHAT WAS WRONG
// src/pages/index.jsx guarded logged-out visitors with an inline prefix check that
// listed five paths (/hotdestinations, /tutorial-videos, /dataroom, /property,
// /pm-connect/) plus a whole-effect bypass for /link and /offer. Everything else was
// pushed to /welcome — including /login, /signup, /forgotPassword, /verifycode/:id,
// /resetpassword/:id, /faq, /intouch and /terms-and-conditions. There was a second,
// longer-looking list a few lines above it that guarded nothing but an inner re-push,
// so the code read as if those paths were allowed when they were not.
//
// Worse, the reset-password link mailed to a user is /resetpassword/<id>?token=...,
// and the token lives in that query string. The guard bounced it to /welcome, and an
// unconditional re-push stripped the query first — so password reset by email could
// not work at all.
//
// THE RULE
// A path is public if it matches exactly, or starts with one of the prefixes. Prefixes
// are used where the route itself is non-exact (/welcome, /signup, /property) or carries
// an id (/link/:linkid, /offer/:token, /verifycode/:id, /resetpassword/:id,
// /pm-connect/:token). Anything not listed needs a session — /calendar/:id included, as
// it was before this change. Keep this in step with the <Route path=...> list in
// src/pages/index.jsx.

export const PUBLIC_EXACT_PATHS = [
  "/",
  "/welcome",
  "/login",
  "/signup",
  "/signupthanks",
  "/forgotPassword",
  "/faq",
  "/intouch",
  "/terms-and-conditions",
  // Both are token/signature-gated on the backend and are opened straight from an
  // email, by someone who is not logged in and never will be.
  "/approve-agency",
  "/delete-agency",
  // Public reference pages that were already exempt. Listed exactly, with the
  // sub-path prefixes below: the old guard used startsWith('/property'), which also
  // let a logged-out visitor onto /propertyEdit.
  "/hotdestinations",
  "/tutorial-videos",
  "/dataroom",
  "/property",
];

export const PUBLIC_PATH_PREFIXES = [
  // Non-exact routes: /welcome and /signup render for anything below them too.
  "/welcome/",
  "/signup/",
  // Email and share links, each carrying its own id or token.
  "/verifycode/",
  "/resetpassword/",
  "/pm-connect/",
  "/link/",
  "/offer/",
  // Sub-paths of the public reference pages above (/hotdestinations/:destination,
  // /property/:id). The trailing slash is what keeps /propertyEdit private.
  "/hotdestinations/",
  "/property/",
];

const normalise = (pathname) => {
  // Trailing slashes are meaningless to the router but would break an exact match.
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

export const isPublicPath = (pathname) => {
  // A security predicate defaults to private. react-router always gives a string, so this
  // only bites if something else calls it — and then "needs a session" is the safe answer.
  if (typeof pathname !== "string" || pathname === "") return false;
  const path = normalise(pathname);
  if (PUBLIC_EXACT_PATHS.indexOf(path) !== -1) return true;
  return PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix) || path === normalise(prefix));
};

export default isPublicPath;
